import { styled } from '@linaria/react';
import { Loader } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useCallback, useState } from 'react';
import {
  useFloraUserPreferences,
  useUpdateFloraUserPreferences,
} from '../../../../api/agents-client/agent-client.hooks';
import { Button } from '../../../../ui-library/button/button';
import { Icon } from '../../../../ui-library/icon/icon';
import { Text } from '../../../../ui-library/typography/typography';
import { AgentViewHeader } from '../agent-view-header';

interface AgentLearnedPreferencesViewProps {
  /** Render inside the settings layout: compact heading, no page padding or scrolling. */
  embedded?: boolean;
}

/**
 * Read-only catalog of always-on preferences Flora has inferred about the user
 * (`learned_preferences`). The user cannot add these — Flora writes them — but
 * each can be removed individually via a lightweight inline confirm. Deletion
 * PUTs the trimmed `learned_preferences` array through the shared preferences
 * update mutation; the backend replaces the list at that key, leaving the
 * user-authored profile fields untouched.
 *
 * @param {AgentLearnedPreferencesViewProps} props - Whether the view renders inside the settings layout
 * @returns {JSX.Element} The "Flora's Preferences" section.
 */
export function AgentLearnedPreferencesView({ embedded = false }: AgentLearnedPreferencesViewProps = {}) {
  const { preferences, isLoading, error } = useFloraUserPreferences();
  const { mutate: updatePreferences, isPending: isSaving } = useUpdateFloraUserPreferences();

  const learnedPreferences = preferences?.learned_preferences ?? [];

  // Track the row awaiting confirmation (and the one actively being removed) by
  // INDEX, not value: two rows can share identical text, and keying by value
  // would confirm/delete every occurrence at once instead of just the clicked
  // row. Before removing, re-check the value at that index still matches what
  // was clicked — the list can refetch between opening the inline confirm and
  // clicking "Remove", which would otherwise delete whatever moved into that
  // slot.
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);
  const [deletingIndex, setDeletingIndex] = useState<number | null>(null);

  const handleRemove = useCallback(
    (index: number, value: string) => {
      if (learnedPreferences[index] !== value) {
        setPendingIndex(null);
        return;
      }
      const remaining = learnedPreferences.toSpliced(index, 1);
      setDeletingIndex(index);
      updatePreferences(
        { learned_preferences: remaining },
        {
          onSuccess: () => {
            notifications.show({ message: 'Preference removed', color: 'green' });
          },
          onError: () => {
            notifications.show({ message: 'Failed to remove preference', color: 'red' });
          },
          onSettled: () => {
            setPendingIndex(null);
            setDeletingIndex(null);
          },
        },
      );
    },
    [learnedPreferences, updatePreferences],
  );

  if (isLoading) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          <AgentViewHeader
            title={embedded ? 'Learned preferences' : "Flora's Preferences"}
            variant={embedded ? 'settings-section' : 'page'}
          />
          <Content>
            <LoadingState>
              <Loader size="sm" color="var(--secondary-base)" />
            </LoadingState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  if (error) {
    return (
      <Container data-embedded={embedded || undefined}>
        <CenterWrap>
          <AgentViewHeader
            title={embedded ? 'Learned preferences' : "Flora's Preferences"}
            variant={embedded ? 'settings-section' : 'page'}
          />
          <Content>
            <EmptyState>
              <Text size="regular" color="var(--ink-lighter)">
                Failed to load preferences.
              </Text>
            </EmptyState>
          </Content>
        </CenterWrap>
      </Container>
    );
  }

  return (
    <Container data-embedded={embedded || undefined}>
      <CenterWrap>
        <AgentViewHeader
          title={embedded ? 'Learned preferences' : "Flora's Preferences"}
          variant={embedded ? 'settings-section' : 'page'}
          subtitle={`${learnedPreferences.length} ${learnedPreferences.length === 1 ? 'preference' : 'preferences'}`}
        />
        <Content>
          <FormContent>
            <Text size="small" color="var(--ink-lighter)" style={{ marginBottom: 16 }}>
              These are always-on preferences Flora has learned about you from your conversations. Remove any that are
              no longer accurate — Flora may learn them again as you keep working together.
            </Text>

            {learnedPreferences.length === 0 ? (
              <EmptyState>
                <Icon name="auto_awesome" color="var(--ink-lighter)" size={40} />
                <Text size="regular" color="var(--ink-lighter)" style={{ marginTop: 12 }}>
                  Flora hasn't learned any preferences yet.
                </Text>
              </EmptyState>
            ) : (
              <PreferenceList>
                {learnedPreferences.map((preference, index) => {
                  const isPending = pendingIndex === index;
                  const isDeleting = deletingIndex === index;
                  return (
                    <PreferenceRow key={`${index}-${preference}`}>
                      <Icon name="auto_awesome" color="var(--secondary-base)" size={18} style={{ flexShrink: 0 }} />
                      <PreferenceText>
                        <Text size="small" color="var(--ink-darkest)">
                          {preference}
                        </Text>
                      </PreferenceText>
                      {isPending ? (
                        <ConfirmGroup>
                          <Text size="tiny" color="var(--ink-lighter)">
                            Remove?
                          </Text>
                          <Button variant="subtle" size="xs" onClick={() => setPendingIndex(null)} disabled={isSaving}>
                            Cancel
                          </Button>
                          <Button
                            variant="warning"
                            size="xs"
                            onClick={() => handleRemove(index, preference)}
                            loading={isDeleting}
                            disabled={isSaving}
                          >
                            Remove
                          </Button>
                        </ConfirmGroup>
                      ) : (
                        <DeleteButton
                          type="button"
                          aria-label={`Remove preference: ${preference}`}
                          onClick={() => setPendingIndex(index)}
                          disabled={isSaving}
                        >
                          <Icon name="delete" size={16} color="var(--ink-lighter)" style={{ cursor: 'pointer' }} />
                        </DeleteButton>
                      )}
                    </PreferenceRow>
                  );
                })}
              </PreferenceList>
            )}
          </FormContent>
        </Content>
      </CenterWrap>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  padding: 40px 0;
  overflow-y: auto;

  &[data-embedded] {
    height: auto;
    padding: 0;
    overflow: visible;
  }
`;

const CenterWrap = styled.div`
  width: 100%;
`;

const Content = styled.div`
  flex: 1;
`;

const FormContent = styled.div`
  width: 100%;
  max-width: 720px;
  margin: 0 auto;

  [data-embedded] & {
    max-width: none;
    margin: 0;
    padding-bottom: 0;
  }
`;

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 200px;
`;

const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  gap: 4px;
  min-height: 200px;
`;

const PreferenceList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const PreferenceRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px 14px;
  border-radius: 10px;
  background: var(--sky-lightest);
  border: 1px solid var(--sky-light);
`;

const PreferenceText = styled.div`
  flex: 1;
  min-width: 0;
  line-height: 1.4;
`;

const ConfirmGroup = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
`;

const DeleteButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  background: none;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  flex-shrink: 0;

  &:hover {
    background: var(--flora-hover-subtle);
  }

  &:focus-visible {
    outline: 2px solid var(--secondary-base);
    outline-offset: 1px;
  }

  &:disabled {
    cursor: default;
    opacity: 0.5;
  }
`;
